import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Eye, EyeOff, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', image: '',
    category: 'General', readTime: '5 min read', featured: false, published: true
  });
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('admin_token');

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const resetForm = () => {
    setFormData({
      title: '', excerpt: '', content: '', image: '',
      category: 'General', readTime: '5 min read', featured: false, published: true
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      image: post.image || '',
      category: post.category || 'General',
      readTime: post.readTime || '5 min read',
      featured: post.featured || false,
      published: post.published !== false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingPost) {
        await axios.put(`${API}/api/admin/blog/${editingPost.id}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
      } else {
        await axios.post(`${API}/api/admin/blog`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
      }
      resetForm();
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await axios.delete(`${API}/api/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
    } catch (err) {
      alert('Failed to delete post');
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/api/admin/blog/${post.id}`, 
        { published: !post.published },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      fetchPosts();
    } catch (err) {
      alert('Failed to update post');
    }
  };

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  return (
    <div className="p-8" data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white">Blog Posts</h1>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="add-blog-btn"
          >
            <Plus size={16} className="mr-2" /> New Post
          </Button>
        )}
      </div>

      {/* Blog Post Form */}
      {showForm && (
        <Card className="bg-gray-900 border-gray-800 mb-8" data-testid="blog-form">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {editingPost ? 'Edit Post' : 'New Blog Post'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Blog post title"
                    data-testid="blog-title-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-2"
                    data-testid="blog-category-select"
                  >
                    <option value="General">General</option>
                    <option value="Guides">Guides</option>
                    <option value="Trends">Trends</option>
                    <option value="Seasonal">Seasonal</option>
                    <option value="Tips">Tips</option>
                    <option value="News">News</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Cover Image URL</Label>
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="https://images.unsplash.com/..."
                    data-testid="blog-image-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Read Time</Label>
                  <Input
                    value={formData.readTime}
                    onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="5 min read"
                    data-testid="blog-readtime-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Excerpt *</Label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-3 min-h-[80px] resize-y"
                  placeholder="Short description shown on blog listing..."
                  data-testid="blog-excerpt-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Content (HTML) *</Label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-3 min-h-[250px] resize-y font-mono text-sm"
                  placeholder="<h2>Section Title</h2>&#10;<p>Paragraph content...</p>"
                  data-testid="blog-content-input"
                  required
                />
              </div>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                    data-testid="blog-featured-checkbox"
                  />
                  <span className="text-gray-300 text-sm">Featured post</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                    data-testid="blog-published-checkbox"
                  />
                  <span className="text-gray-300 text-sm">Published</span>
                </label>
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700"
                  disabled={submitting}
                  data-testid="blog-submit-btn"
                >
                  {submitting ? 'Saving...' : (editingPost ? 'Update Post' : 'Create Post')}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="border-gray-700 text-gray-300">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      {posts.length === 0 && !showForm ? (
        <div className="text-center py-16">
          <p className="text-gray-400 mb-4">No blog posts yet. Create your first post!</p>
          <Button onClick={() => setShowForm(true)} className="bg-orange-600 hover:bg-orange-700">
            <Plus size={16} className="mr-2" /> Create Post
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.id} className="bg-gray-900 border-gray-800" data-testid={`blog-card-${post.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {post.image && (
                      <img src={post.image} alt={post.title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-white font-medium truncate">{post.title}</h3>
                        {post.featured && <Star size={14} className="text-yellow-500 fill-yellow-500 flex-shrink-0" />}
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center space-x-3 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">{post.category}</span>
                        <span className="text-xs text-gray-500">{post.readTime}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${post.published !== false ? 'bg-green-900/30 text-green-400' : 'bg-gray-800 text-gray-500'}`}>
                          {post.published !== false ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => togglePublished(post)}
                      className="text-gray-400 hover:text-white"
                      title={post.published !== false ? 'Unpublish' : 'Publish'}
                    >
                      {post.published !== false ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(post)}
                      className="text-gray-400 hover:text-orange-400"
                      title="Edit"
                      data-testid={`edit-blog-${post.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(post.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      title="Delete"
                      data-testid={`delete-blog-${post.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
